/**
 * One place that answers "is this a phone, and what does that cost the user?".
 *
 * `/vendor/mobile-tier.js` runs before the bundle and, on phones and tablets,
 * swaps the 6-stem engine for a 64MB 2-stem one (vocals / instrumental) and
 * turns tab capture off. Until now the two flags it publishes were read in
 * ytCapture.ts only, so every OTHER surface — the splitter funnel above all —
 * happily promised six stems on a device that can only produce two.
 *
 * Nothing here detects anything itself: the bootstrap script owns that decision
 * (UA + userAgentData.mobile + an iPad test + a coarse-pointer check) and this
 * module only reports it, so the engine we run and the copy we print can never
 * disagree.
 */

declare global {
  interface Window {
    /** The downgraded engine id the bootstrap picked. Absent on desktop. */
    __RL_MOBILE_TIER__?: string;
    /** Set by the same script: getDisplayMedia can't capture tab audio here. */
    __RL_NO_CAPTURE__?: boolean;
  }
}

/**
 * True only on phones and tablets — the devices routed to the 2-stem engine.
 *
 * Deliberately NOT the same question as "can this browser capture a tab": a
 * desktop browser without getDisplayMedia also cannot capture, but it IS a
 * computer, so "use a computer instead" would be nonsense advice there.
 */
export function isMobileTier(): boolean {
  return typeof window !== 'undefined' && !!window.__RL_MOBILE_TIER__;
}

/** What a 2-stem run actually returns, in rack order. */
export const MOBILE_STEM_COUNT = 2;
export const DESKTOP_STEM_COUNT = 6;

/**
 * The `other` stem is the app's "Solo & Lead" everywhere except a 2-stem run,
 * where the model predicts vocals and the rest is arrived at by subtraction —
 * it is the whole instrumental, and calling it "Solo & Lead" tells a phone user
 * their guitar and drums are missing from a lane that contains both.
 */
export const TWO_STEM_OTHER_NAME = 'Instrumental';

/**
 * A run came back with only vocals and `other` filled in — i.e. the 2-stem
 * engine produced it. Keyed off the RESULT rather than the device so a stem
 * count is never claimed that the audio doesn't back up.
 */
export function isTwoStemResult(empty: Set<string>): boolean {
  return (
    !empty.has('vocals') &&
    !empty.has('other') &&
    ['drums', 'bass', 'piano', 'guitar'].every((s) => empty.has(s))
  );
}

/** riffloop.app's Studio origin — localhost keeps the hand-off testable. */
export const STUDIO_ORIGIN =
  typeof location !== 'undefined' && /^(localhost|127\.0\.0\.1)$/.test(location.hostname)
    ? location.origin
    : 'https://studio.riffloop.app';

export type DesktopHandoff =
  /** Nothing to carry: the file can't travel, so the link is just the page. */
  | { kind: 'file' }
  /** A pasteable source URL the desktop can pick straight back up. */
  | { kind: 'link'; url: string };

/**
 * The URL we hand to the share sheet.
 *
 * `?d=1` marks a phone→computer hand-off so the arrival is distinguishable from
 * an ordinary visit; `?src=` carries the song link, which StudioNext reads on
 * boot and drops straight into the capture flow. A file hand-off carries
 * neither — there is nothing to carry — so it lands on the splitter's own
 * dropzone, which is where they have to pick the file again anyway.
 */
export function desktopUrl(h: DesktopHandoff): string {
  if (h.kind === 'link') {
    return `${STUDIO_ORIGIN}/?src=${encodeURIComponent(h.url)}&d=1`;
  }
  return `${STUDIO_ORIGIN}/splitter?d=1`;
}

export type SendResult = 'shared' | 'copied' | 'dismissed' | 'failed';

/**
 * Send a link off this device.
 *
 * The share sheet is the whole point: on iOS and Android it is the one surface
 * that already knows how to reach the user's own computer — AirDrop, Messages,
 * Mail, whatever they use — without us asking for an address
 * (we never ask for an email). Copy is the fallback for the browsers that
 * don't have it, and the return value tells the caller which happened so the
 * button can say something true afterwards.
 */
export async function sendLink(url: string, title: string, text: string): Promise<SendResult> {
  if (typeof navigator !== 'undefined' && navigator.share) {
    try {
      await navigator.share({ title, text, url });
      return 'shared';
    } catch (e) {
      // AbortError = the sheet was dismissed. That is a decision, not a
      // failure, and must not fall through to silently copying instead.
      if (e && typeof e === 'object' && (e as { name?: string }).name === 'AbortError') return 'dismissed';
      // Anything else (NotAllowedError outside a gesture, an unsupported
      // payload) still deserves the link, so fall through to the clipboard.
    }
  }
  return (await copyLink(url)) ? 'copied' : 'failed';
}

/** Clipboard write with the pre-async-clipboard fallback still in place. */
export async function copyLink(url: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
      return true;
    }
  } catch {
    /* insecure context, or permission refused — try the old way */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = url;
    ta.setAttribute('readonly', '');
    ta.style.cssText = 'position:fixed;top:-9999px;opacity:0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}
